import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { useNavigate, useParams } from "react-router-dom";
import { settingsApi } from "../../../api/settings";
import { projectsApi } from "../../../api/projects";
import { formatApiError } from "../../../api/client";
import { useProjects } from "../../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../../utils/projectModules";
import LandingCanvas from "../landingEditor/LandingCanvas";
import { mergeSettings } from "../landingEditor/config";
import { getLandingPreviewDevice } from "./previewDevices";
import {
  buildQuickDesignChatGptPrompt,
  missingStockBackgrounds,
} from "./quickDesignAssistant";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const MAX_IMPORT_BYTES = 2 * 1024 * 1024;

function sessionKey(projectId) {
  return `webprofit:quick-design:${String(projectId || "")}`;
}

function packageText(portable) {
  return portable ? JSON.stringify(portable, null, 2) : "";
}

function parseJsonProposal(rawValue) {
  const raw = String(rawValue || "").trim();
  if (!raw) throw new Error("Pegá o importá una configuración antes de validarla.");
  let candidate = raw;
  const fence = raw.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fence) candidate = fence[1].trim();
  else if (/```/.test(raw)) {
    throw new Error("Usá JSON puro o un único bloque Markdown ```json ... ``` sin texto adicional.");
  }
  try {
    return JSON.parse(candidate);
  } catch (error) {
    const message = String(error?.message || "JSON inválido");
    const position = message.match(/position\s+(\d+)/i)?.[1];
    if (position) {
      const offset = Number(position);
      const before = candidate.slice(0, Number.isFinite(offset) ? offset : 0);
      const line = before.split("\n").length;
      throw new Error(`JSON inválido cerca de la línea ${line}: ${message}`);
    }
    throw new Error(`JSON inválido: ${message}`);
  }
}

function statusLabel(state) {
  if (!state) return "Cargando estado…";
  if (!state.hasPublishedVersion) return "Todavía sin publicar";
  if (state.hasUnpublishedChanges) return "Borrador con cambios sin publicar";
  return "Borrador y versión publicada sincronizados";
}

function ErrorList({ title, items, severity = "error" }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <Alert severity={severity} role="alert" sx={{ alignItems: "flex-start" }}>
      <Typography sx={{ fontWeight: 900, mb: 0.5 }}>{title}</Typography>
      <Stack component="ul" spacing={0.4} sx={{ pl: 2.2, m: 0 }}>
        {items.slice(0, 12).map((item, index) => (
          <Typography component="li" variant="body2" key={`${item.path || "item"}-${index}`}>
            {item.path ? <strong>{item.path.replace(/^configuration\./, "")}: </strong> : null}
            {item.message || "Valor inválido"}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
}

function ChangeSummary({ summary }) {
  if (!summary) return null;
  if (!summary.changed) {
    return <Alert severity="info">La propuesta no produce cambios efectivos sobre el borrador actual.</Alert>;
  }
  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <Stack spacing={1.25}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
          <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Resumen antes de aplicar</Typography>
          <Chip size="small" label={`${summary.totalChanges} cambios detectados`} />
        </Stack>
        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
          {(summary.groups || []).map((group) => (
            <Chip key={group.label} size="small" variant="outlined" label={`${group.label}: ${group.count}`} />
          ))}
        </Stack>
        <Divider />
        <Stack spacing={0.7}>
          {(summary.items || []).slice(0, 18).map((item, index) => (
            <Box key={`${item.path}-${index}`}>
              <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.72)" }}>
                {item.path}
              </Typography>
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                {String(item.before ?? "")} → {String(item.after ?? "")}
              </Typography>
            </Box>
          ))}
          {summary.truncated ? (
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)" }}>
              El resumen está abreviado; la validación y la aplicación incluyen el snapshot completo.
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
}

export default function ProjectSiteRedirect() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:700px)");
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [portable, setPortable] = React.useState(null);
  const [state, setState] = React.useState(null);
  const [settings, setSettings] = React.useState(null);
  const [quickOpen, setQuickOpen] = React.useState(false);
  const [proposalText, setProposalText] = React.useState(() => {
    try {
      return window.sessionStorage.getItem(sessionKey(projectId)) || "";
    } catch {
      return "";
    }
  });
  const [validation, setValidation] = React.useState(null);
  const [validationBusy, setValidationBusy] = React.useState(false);
  const [applyBusy, setApplyBusy] = React.useState(false);
  const [parseError, setParseError] = React.useState("");
  const [manualCopy, setManualCopy] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewDevice, setPreviewDevice] = React.useState("desktop");
  const [confirmApplyOpen, setConfirmApplyOpen] = React.useState(false);
  const [stockChoice, setStockChoice] = React.useState("ask");
  const fileInputRef = React.useRef(null);
  const proposalInputRef = React.useRef(null);

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [projects, projectId, activeProject],
  );
  const effectiveProject = project || contextProject;
  const landingEnabled = isProjectModuleEnabled(effectiveProject, "landing");
  const productsEnabled = isProjectModuleEnabled(effectiveProject, "products");
  const currentForm = React.useMemo(() => mergeSettings(settings || {}), [settings]);
  const previewForm = React.useMemo(
    () => mergeSettings(validation?.canonicalConfiguration || {}),
    [validation?.canonicalConfiguration],
  );
  const device = getLandingPreviewDevice(previewDevice);
  const missingBackgrounds = React.useMemo(() => missingStockBackgrounds(portable), [portable]);
  const stockDecisionPending = missingBackgrounds.length > 0 && stockChoice === "ask";
  const assistantPrompt = React.useMemo(
    () => stockDecisionPending
      ? ""
      : buildQuickDesignChatGptPrompt(portable, {
        includeStockImages: stockChoice === "yes",
        missingBackgrounds,
      }),
    [portable, stockChoice, missingBackgrounds, stockDecisionPending],
  );

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    try {
      if (proposalText) window.sessionStorage.setItem(sessionKey(projectId), proposalText);
      else window.sessionStorage.removeItem(sessionKey(projectId));
    } catch {
      // Session storage es sólo una protección contra pérdida accidental.
    }
  }, [projectId, proposalText]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [portableData, projectData] = await Promise.all([
        settingsApi.exportPortable(projectId),
        projectsApi.get(projectId).catch(() => null),
      ]);
      setPortable(portableData?.portable || null);
      setState(portableData?.state || null);
      setSettings(portableData?.settings || null);
      if (projectData?.project) {
        setProject(projectData.project);
        setActiveProject(projectData.project);
      }
    } catch (err) {
      setError(formatApiError(err, "No se pudo cargar la configuración de Página"));
    } finally {
      setLoading(false);
    }
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openVisualEditor = () => navigate(`/editor-web/${encodeURIComponent(projectId)}`);

  const copyConfiguration = async () => {
    const text = packageText(portable);
    if (!text) return;
    setManualCopy(false);
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(text);
      setMessage("Configuración copiada. Podés pegarla en tu herramienta externa.");
    } catch {
      setManualCopy(true);
      setMessage("El navegador bloqueó el portapapeles. Copiá manualmente desde el bloque mostrado.");
    }
  };

  const downloadConfiguration = () => {
    const text = packageText(portable);
    if (!text) return;
    const blob = new Blob([text], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `webprofit-diseno-${String(effectiveProject?.slug || projectId || "sitio")}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    setMessage("Configuración descargada en JSON.");
  };

  const copyAssistantPrompt = async () => {
    if (!assistantPrompt) return;
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(assistantPrompt);
      setMessage("Prompt copiado. Pegalo en ChatGPT y traé de vuelta solamente el JSON que te devuelva.");
    } catch {
      setMessage("El navegador bloqueó el portapapeles. Copiá manualmente el prompt desde el bloque mostrado.");
    }
  };

  const updateProposalText = (value) => {
    setProposalText(value);
    setValidation(null);
    setParseError("");
    setPreviewOpen(false);
  };

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMPORT_BYTES) {
      setParseError("El archivo supera el máximo permitido de 2 MB.");
      return;
    }
    const extension = String(file.name || "").toLowerCase();
    if (!extension.endsWith(".json") && !extension.endsWith(".txt")) {
      setParseError("Importá un archivo .json o .txt que contenga el contrato JSON.");
      return;
    }
    try {
      const text = await file.text();
      if (!text.trim()) throw new Error("El archivo está vacío.");
      updateProposalText(text);
      setQuickOpen(true);
      setMessage(`Archivo ${file.name} cargado. Validalo antes de previsualizar o aplicar.`);
    } catch (err) {
      setParseError(err.message || "No se pudo leer el archivo importado.");
    }
  };

  const validateProposal = async () => {
    let parsed;
    try {
      parsed = parseJsonProposal(proposalText);
      setParseError("");
    } catch (err) {
      setValidation(null);
      setParseError(err.message || "JSON inválido");
      return null;
    }
    try {
      setValidationBusy(true);
      setError("");
      const response = await settingsApi.validatePortable(projectId, parsed);
      const nextValidation = response?.validation || null;
      setValidation(nextValidation);
      if (nextValidation?.valid) {
        setMessage(nextValidation?.summary?.changed
          ? "Configuración válida. Revisá el resumen o previsualizá antes de aplicar."
          : "La propuesta es válida y ya coincide con el borrador actual.");
      }
      return { parsed, validation: nextValidation };
    } catch (err) {
      setError(formatApiError(err, "No se pudo validar la configuración"));
      return null;
    } finally {
      setValidationBusy(false);
    }
  };

  const applyProposal = async (options = {}) => {
    let parsed;
    try {
      parsed = parseJsonProposal(proposalText);
      setParseError("");
    } catch (err) {
      setParseError(err.message || "JSON inválido");
      return;
    }
    try {
      setApplyBusy(true);
      setError("");
      const response = await settingsApi.applyPortable(projectId, parsed, options);
      setMessage(response?.message || "Diseño aplicado al borrador. La web pública no cambia hasta publicar.");
      setProposalText("");
      setValidation(null);
      setPreviewOpen(false);
      setConfirmApplyOpen(false);
      await load();
    } catch (err) {
      if (err?.status === 409) {
        setMessage("");
        setError(formatApiError(err, "El borrador cambió antes de aplicar. Volvé a validar la propuesta."));
        await validateProposal();
      } else {
        setError(formatApiError(err, "No se pudo aplicar el diseño"));
      }
    } finally {
      setApplyBusy(false);
    }
  };

  const requestApply = () => {
    if (!validation?.valid) return;
    if (["changed", "unbased"].includes(validation?.conflict?.state)) {
      setConfirmApplyOpen(true);
      return;
    }
    applyProposal();
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
        <Stack spacing={1.2} alignItems="center">
          <CircularProgress sx={{ color: BRAND.orange }} />
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Preparando Página…</Typography>
        </Stack>
      </Box>
    );
  }

  if (effectiveProject && !landingEnabled) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">Este proyecto no tiene habilitado el módulo de Página web.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5, lg: 3 }, maxWidth: 1440, mx: "auto" }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>Página</Typography>
            <Typography sx={{ color: "rgba(25,36,47,.68)", mt: 0.5 }}>
              Elegí cómo querés trabajar sobre el mismo borrador de tu web.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={statusLabel(state)} variant="outlined" />
            <Chip label={`Borrador v${state?.draftRevision || 1}`} />
          </Stack>
        </Stack>

        {error ? <Alert severity="error" role="alert">{error}</Alert> : null}
        {message ? <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert> : null}

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
          <Card variant="outlined" sx={{ flex: 1, p: 2.25, borderRadius: 3 }}>
            <Stack spacing={1.4} height="100%">
              <EditRoundedIcon sx={{ color: BRAND.orange }} />
              <Typography variant="h6" sx={{ fontWeight: 950 }}>Editar visualmente</Typography>
              <Typography sx={{ color: "rgba(25,36,47,.66)", lineHeight: 1.55 }}>
                Entrá al Landing Editor actual para editar secciones, imágenes, estilos y responsive de forma visual.
              </Typography>
              <Button variant="contained" onClick={openVisualEditor} sx={{ mt: "auto", bgcolor: BRAND.orange, fontWeight: 900, alignSelf: "flex-start" }}>
                Editar visualmente
              </Button>
            </Stack>
          </Card>

          <Card variant="outlined" sx={{ flex: 1, p: 2.25, borderRadius: 3 }}>
            <Stack spacing={1.4} height="100%">
              <AutoAwesomeRoundedIcon sx={{ color: "#6957E8" }} />
              <Typography variant="h6" sx={{ fontWeight: 950 }}>Diseño rápido</Typography>
              <Typography sx={{ color: "rgba(25,36,47,.66)", lineHeight: 1.55 }}>
                Exportá la configuración, trabajala con una herramienta externa y volvé a cargarla. Previsualizar no guarda; aplicar sólo actualiza el borrador.
              </Typography>
              <Button variant="outlined" onClick={() => setQuickOpen((value) => !value)} sx={{ mt: "auto", fontWeight: 900, alignSelf: "flex-start" }}>
                {quickOpen ? "Ocultar Diseño rápido" : "Abrir Diseño rápido"}
              </Button>
            </Stack>
          </Card>
        </Stack>

        <Card variant="outlined" sx={{ overflow: "hidden", borderRadius: 3 }}>
          <Box sx={{ px: 2, py: 1.4, bgcolor: "rgba(25,36,47,.035)" }}>
            <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Vista del borrador actual</Typography>
          </Box>
          <Box sx={{ maxHeight: 360, overflow: "hidden", pointerEvents: "none", bgcolor: "#fff" }}>
            <LandingCanvas form={currentForm} device="desktop" productsEnabled={productsEnabled} activeSection="" studioMode={false} />
          </Box>
        </Card>

        {quickOpen ? (
          <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2.25 }, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Diseño rápido</Typography>
                <Typography sx={{ color: "rgba(25,36,47,.68)", mt: 0.5 }}>
                  El paquete incluye contenido público configurado —como teléfono, dirección, textos o redes— pero nunca credenciales, tokens ni secretos.
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap flexWrap="wrap">
                <Button startIcon={<ContentCopyRoundedIcon />} onClick={copyConfiguration} disabled={!portable}>Copiar configuración</Button>
                <Button startIcon={<DownloadRoundedIcon />} onClick={downloadConfiguration} disabled={!portable}>Descargar configuración</Button>
                <Button
                  startIcon={<UploadFileRoundedIcon />}
                  onClick={() => proposalInputRef.current?.focus()}
                >
                  Pegar configuración
                </Button>
                <Button startIcon={<FileUploadRoundedIcon />} onClick={() => fileInputRef.current?.click()}>Importar archivo</Button>
                <Button startIcon={validationBusy ? <CircularProgress size={17} /> : <RefreshRoundedIcon />} onClick={validateProposal} disabled={!proposalText.trim() || validationBusy || applyBusy}>Validar</Button>
                <Button startIcon={<PreviewRoundedIcon />} onClick={() => setPreviewOpen(true)} disabled={!validation?.valid}>Previsualizar</Button>
                <Button variant="contained" startIcon={applyBusy ? <CircularProgress size={17} color="inherit" /> : <CheckCircleRoundedIcon />} onClick={requestApply} disabled={!validation?.valid || applyBusy} sx={{ bgcolor: BRAND.orange, fontWeight: 900 }}>
                  Aplicar diseño
                </Button>
                <input ref={fileInputRef} type="file" accept=".json,.txt,application/json,text/plain" hidden onChange={importFile} />
              </Stack>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)" }}>
                Previsualizar y Aplicar diseño se habilitan después de una validación real del backend. Aplicar modifica sólo el borrador; no publica la web.
              </Typography>

              <TextField
                label="Configuración actual exportable"
                value={packageText(portable)}
                multiline
                minRows={6}
                maxRows={12}
                fullWidth
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-label": "Configuración actual exportable", spellCheck: false }}
                helperText={manualCopy
                  ? "El navegador bloqueó el portapapeles. Seleccioná y copiá este contenido manualmente."
                  : "Este es el borrador vigente con su manifest y reglas. Copiar y Descargar usan exactamente este mismo paquete."}
                sx={{ "& textarea": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5 } }}
              />

              {missingBackgrounds.length ? (
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: "rgba(105,87,232,.035)" }}>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>¿Querés que ChatGPT busque y agregue imágenes de stock?</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.68)", mt: 0.35 }}>
                        Detectamos {missingBackgrounds.length} {missingBackgrounds.length === 1 ? "sección visible sin imagen de fondo" : "secciones visibles sin imagen de fondo"}. Si elegís Sí, ChatGPT buscará imágenes apropiadas para el rubro y devolverá el siteSettings con las URLs ya colocadas. No tenés que buscar ni pegar links manualmente.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant={stockChoice === "yes" ? "contained" : "outlined"}
                        onClick={() => setStockChoice("yes")}
                        sx={stockChoice === "yes" ? { bgcolor: BRAND.orange, fontWeight: 900 } : { fontWeight: 900 }}
                      >
                        Sí, que ChatGPT las busque
                      </Button>
                      <Button
                        variant={stockChoice === "no" ? "contained" : "outlined"}
                        onClick={() => setStockChoice("no")}
                        sx={stockChoice === "no" ? { bgcolor: BRAND.dark, fontWeight: 900 } : { fontWeight: 900 }}
                      >
                        No, mantener fondos actuales
                      </Button>
                    </Stack>
                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                      {missingBackgrounds.map((item) => (
                        <Chip key={item.key} size="small" variant="outlined" label={item.label} />
                      ))}
                    </Stack>
                    {stockChoice === "yes" ? (
                      <Alert severity="info">
                        El prompt autoriza a ChatGPT a usar búsqueda web/imágenes y le exige insertar por su cuenta URLs públicas HTTPS directamente en los campos admitidos del siteSettings. Puede elegir foto de stock, textura/patrón o un fondo sólido según convenga al diseño.
                      </Alert>
                    ) : null}
                  </Stack>
                </Card>
              ) : (
                <Alert severity="success">Las secciones visibles ya tienen fondos configurados. El prompt conserva esas imágenes salvo que le pidas explícitamente cambiarlas.</Alert>
              )}

              <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                <Stack spacing={1.2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Prompt listo para ChatGPT</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.68)", mt: 0.25 }}>
                        Incluye el formato completo, las reglas del contrato y tu configuración actual. Si autorizaste stock, ChatGPT también busca las imágenes y coloca los links dentro del JSON.
                      </Typography>
                    </Box>
                    <Button startIcon={<AutoAwesomeRoundedIcon />} variant="contained" onClick={copyAssistantPrompt} disabled={!assistantPrompt || stockDecisionPending} sx={{ bgcolor: "#6957E8", fontWeight: 900 }}>
                      Copiar prompt para ChatGPT
                    </Button>
                  </Stack>
                  <TextField
                    label="Prompt + configuración"
                    value={assistantPrompt}
                    multiline
                    minRows={7}
                    maxRows={14}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    inputProps={{ "aria-label": "Prompt listo para ChatGPT", spellCheck: false }}
                    helperText={stockDecisionPending
                      ? "Elegí Sí o No en imágenes de stock para generar el prompt definitivo."
                      : "Pegá este bloque completo en ChatGPT y después pegá acá únicamente el JSON que te devuelva. Si elegiste Sí, ChatGPT se ocupa de buscar e insertar las imágenes."}
                    sx={{ "& textarea": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5 } }}
                  />
                </Stack>
              </Card>

              <TextField
                inputRef={proposalInputRef}
                label="Propuesta importada"
                value={proposalText}
                onChange={(event) => updateProposalText(event.target.value)}
                placeholder="Pegá aquí el JSON devuelto por la herramienta externa."
                multiline
                minRows={8}
                maxRows={20}
                fullWidth
                error={Boolean(parseError)}
                helperText={parseError || "Este bloque es independiente de la configuración actual hasta que lo valides y apliques."}
                inputProps={{ spellCheck: false, "aria-label": "Propuesta de configuración importada" }}
                sx={{ "& textarea": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 } }}
              />

              <ErrorList title="Corregí estos errores antes de continuar" items={validation?.errors} />
              <ErrorList title="Normalizaciones y advertencias" items={validation?.warnings} severity="warning" />

              {validation?.conflict?.state === "changed" ? (
                <Alert severity="warning">
                  <Typography sx={{ fontWeight: 900 }}>La página cambió desde que exportaste esta configuración.</Typography>
                  <Typography variant="body2" sx={{ mt: 0.35 }}>
                    Revisá el resumen contra el borrador actual. Si decidís continuar, WebProfit pedirá una confirmación explícita antes de reemplazarlo.
                  </Typography>
                </Alert>
              ) : null}
              {validation?.conflict?.state === "unbased" ? (
                <Alert severity="warning">
                  La propuesta no trae una base verificable. Puede previsualizarse, pero aplicar requerirá confirmar que querés reemplazar el borrador actual.
                </Alert>
              ) : null}

              {validation?.valid ? <ChangeSummary summary={validation.summary} /> : null}
            </Stack>
          </Card>
        ) : null}
      </Stack>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullScreen={isMobile} fullWidth maxWidth="xl">
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>Previsualización de la propuesta validada</DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: "#E9ECF0" }}>
          <Stack spacing={1.2} sx={{ p: { xs: 1, sm: 1.5 }, alignItems: "center" }}>
            <Alert severity="info" sx={{ width: "100%", maxWidth: 980 }}>
              Esta vista reproduce la landing principal. Cambios de páginas de catálogo, detalle de producto o notificaciones quedan identificados por path en el resumen previo a aplicar.
            </Alert>
            <ToggleButtonGroup
              exclusive
              value={previewDevice}
              onChange={(_event, value) => value && setPreviewDevice(value)}
              size="small"
              aria-label="Dispositivo de previsualización"
            >
              <ToggleButton value="desktop">Escritorio</ToggleButton>
              <ToggleButton value="tablet">Tablet</ToggleButton>
              <ToggleButton value="mobile">Celular</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ width: "100%", overflowX: "auto", pb: 1 }}>
              <Box sx={{ width: `${device.width}px`, mx: "auto", bgcolor: "#fff", boxShadow: "0 14px 42px rgba(25,36,47,.15)", overflow: "hidden" }}>
                <LandingCanvas form={previewForm} device={device.id} productsEnabled={productsEnabled} activeSection="" studioMode={false} />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
          <Button variant="contained" onClick={requestApply} disabled={!validation?.valid || applyBusy} sx={{ bgcolor: BRAND.orange, fontWeight: 900 }}>Aplicar diseño</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmApplyOpen} onClose={() => !applyBusy && setConfirmApplyOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>Confirmar reemplazo del borrador</DialogTitle>
        <DialogContent>
          <Stack spacing={1.3}>
            <Alert severity="warning">
              {validation?.conflict?.state === "changed"
                ? "El borrador cambió desde la exportación. Esta acción aplicará el snapshot importado sobre la versión actual después de una última validación del backend."
                : "La propuesta no tiene una base verificable. Esta acción reemplazará explícitamente el borrador actual con el snapshot importado validado."}
            </Alert>
            <ChangeSummary summary={validation?.summary} />
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.68)" }}>
              La web pública no se publica con esta acción. Seguirá mostrando la última versión publicada hasta que uses Publicar en el editor visual.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 1 }}>
          <Button onClick={() => setConfirmApplyOpen(false)} disabled={applyBusy}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={applyBusy}
            onClick={() => applyProposal({
              forceConflict: validation?.conflict?.state === "changed",
              confirmReplace: validation?.conflict?.state === "unbased",
            })}
            sx={{ bgcolor: BRAND.orange, fontWeight: 900 }}
          >
            {applyBusy ? "Aplicando…" : "Confirmar y aplicar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
